import React, { FC, useCallback, useEffect, useState } from 'react';

import { SORT_DIRECTION, TableActionHeader, TableCheckbox, TableHeader, TableRow } from '@web/components/table';
import { Typography, Button, Icon } from '@packages/ui/shared';
import { useAuthState } from '@packages/contexts/auth';
import { getLogger } from '@packages/core/logging';
import { httpClient } from '@web/services/singletons';
import type { Qmr } from '@packages/models/api';

const logger = getLogger('qmr');

type SelectedQmrItem = Pick<Qmr, 'isNqr' | 'qmrId'>;

interface QmrTableHeadProps {
    selectAll?: boolean;
    selectedCount?: number;
    onSelectAllChange(event: React.ChangeEvent<HTMLInputElement>): void;
    sortOrder?: string | null;
    sortDirection?: SORT_DIRECTION | null;
    onSort(sortBy: string, sortDirection: SORT_DIRECTION): void;
    onActionPress?(actionId: string): void;
    selectedItems?: SelectedQmrItem[] | null;
    isDsqmStatusPresent?: boolean;
    showNQQmrs?: boolean;
}

export const QmrTableHead: FC<QmrTableHeadProps> = ({
    selectAll,
    onSelectAllChange,
    sortOrder,
    sortDirection,
    onSort,
    selectedCount,
    onActionPress,
    selectedItems,
    isDsqmStatusPresent,
    showNQQmrs = false,
}) => {
    const { account } = useAuthState();
    const [canEscalateQmr, setCanEscalateQmr] = useState(false);

    const handleSort = useCallback(
        (sortBy: string, sortDirection: SORT_DIRECTION) => {
            onSort(sortBy, sortDirection);
        },
        [onSort]
    );

    const handleActionPress = useCallback(
        (actionId: string) => {
            if (!onActionPress) {
                return;
            }

            onActionPress(actionId);
        },
        [onActionPress]
    );

    useEffect(() => {
        const hasSelectedNqr = Boolean(selectedItems?.some((item) => item?.isNqr));

        if (showNQQmrs || hasSelectedNqr || selectedItems?.length !== 1) {
            setCanEscalateQmr(false);
            return;
        }

        // We are using a separate endpoint to fetch the QMR capabilities for the selected QMR
        // because the info is not included in the QMR API list response (we removed it as of Feb 2024 for performance).
        // See https://github.com/SubaruTechShare/sts-backend/pull/1372
        let _canEscalateQmr = false;
        const config = {
            method: 'GET' as const,
            ignoreCache: true,
        };

        httpClient
            .orchestrateRequest<{ capabilities: { canEscalateQmr: boolean }; message?: string }>({
                ...config,
                url: `/qmrs/capabilities/${selectedItems[0]?.qmrId}`,
            })
            .then((qmrCapabilityResponse) => {
                if (qmrCapabilityResponse.success) {
                    _canEscalateQmr = qmrCapabilityResponse.data?.capabilities.canEscalateQmr;
                    setCanEscalateQmr(_canEscalateQmr);
                } else {
                    throw new Error(qmrCapabilityResponse.data?.message);
                }
            })
            .catch((e) => {
                setCanEscalateQmr(_canEscalateQmr);
                alert('Error fetching QMR permissions. Some actions may not be available. Error: ' + e);
                logger.error(`Failed to fetch QMR permissions for QMR [${selectedItems[0]?.qmrId}]`, {
                    error: e,
                    qmrId: selectedItems[0]?.qmrId,
                });
            });
    }, [selectedItems, showNQQmrs]);

    const hasSelectedNqr = Boolean(selectedItems?.some((item) => item?.isNqr));
    const isNqrSelection = showNQQmrs || hasSelectedNqr;
    const canExportCsv = Boolean(account?.systemCapabilities.canExportQMRsAsCsv);
    const canAddToInvestigation = !isNqrSelection && Boolean(account?.investigationCapabilities.editAnInvestigation);
    const canEscalateSelectedQmr = !isNqrSelection && canEscalateQmr && selectedCount === 1;
    const filteredPermissions = Boolean(
        account?.baseRoleIds?.some((baseRole) => baseRole === 'NASI_SIA_ADMIN' || baseRole === 'RETAILER_ADMIN')
    );
    const displayLock = account?.systemCapabilities.viewQmrRestrictedOnList || filteredPermissions;

    return (
        <TableRow>
            {(canExportCsv || canAddToInvestigation || canEscalateSelectedQmr) && (
                <TableHeader fixed hideBorder={!selectAll}>
                    <TableCheckbox checked={selectAll} onChange={onSelectAllChange} iconName="minus" />
                </TableHeader>
            )}
            {selectAll ? (
                <>
                    <TableActionHeader colspan={10} selectedCount={selectedCount}>
                        {canExportCsv && (
                            <Button
                                variant="ghost-blue"
                                iconLeft={<Icon name="download" color="blueOne" size={20} />}
                                onPress={() => {
                                    handleActionPress('DOWNLOAD');
                                }}
                            >
                                Export to CSV
                            </Button>
                        )}
                        {canAddToInvestigation && (
                            <Button
                                variant="ghost-blue"
                                iconLeft={<Icon name="folders" color="blueOne" size={20} />}
                                onPress={() => {
                                    handleActionPress('ADD_TO_INVESTIGATION');
                                }}
                            >
                                Add to Investigation
                            </Button>
                        )}
                        {canEscalateSelectedQmr && (
                            <Button
                                variant="ghost-blue"
                                iconLeft={<Icon name="escalation" color="blueOne" size={20} />}
                                onPress={() => {
                                    handleActionPress('ESCALATE');
                                }}
                            >
                                Escalate
                            </Button>
                        )}
                    </TableActionHeader>
                </>
            ) : (
                <>
                    {/* Placeholder Header for Restricted status */}
                    {(account?.systemCapabilities.viewQmrRestrictedOnList || displayLock) && (
                        <TableHeader></TableHeader>
                    )}

                    <TableHeader
                        sortable
                        sortDirection={sortOrder === 'UPDATED' ? sortDirection : undefined}
                        onSort={(_event, sortDirection) => {
                            handleSort('UPDATED', sortDirection);
                        }}
                    >
                        <Typography variant="label" numberOfLines={1}>
                            Updated
                        </Typography>
                    </TableHeader>
                    <TableHeader
                        sortDirection={sortOrder === 'QMR_ID' ? sortDirection : undefined}
                        onSort={(_event, sortDirection) => {
                            handleSort('QMR_ID', sortDirection);
                        }}
                    >
                        <Typography variant="label" numberOfLines={1}>
                            Report ID
                        </Typography>
                    </TableHeader>
                    <TableHeader
                        sortDirection={sortOrder === 'VIN' ? sortDirection : undefined}
                        onSort={(_event, sortDirection) => {
                            handleSort('VIN', sortDirection);
                        }}
                    >
                        <Typography variant="label" numberOfLines={1}>
                            VIN
                        </Typography>
                    </TableHeader>
                    <TableHeader
                        sortable
                        sortDirection={sortOrder === 'QMR_STATUS' ? sortDirection : undefined}
                        onSort={(_event, sortDirection) => {
                            handleSort('QMR_STATUS', sortDirection);
                        }}
                    >
                        <Typography variant="label" numberOfLines={1}>
                            {showNQQmrs ? 'DSQM Status' : 'QMR Status'}
                        </Typography>
                    </TableHeader>
                    {isDsqmStatusPresent && (
                        <TableHeader
                            sortable
                            sortDirection={sortOrder === 'QMR_STATUS' ? sortDirection : undefined}
                            onSort={(_event, sortDirection) => {
                                handleSort('QMR_STATUS', sortDirection);
                            }}
                        >
                            <Typography variant="label" numberOfLines={1}>
                                Feedback Status
                            </Typography>
                        </TableHeader>
                    )}
                    {account?.systemCapabilities.viewQmrRoleReviewOnList && (
                        <TableHeader
                            sortDirection={sortOrder === 'ROLE' ? sortDirection : undefined}
                            onSort={(_event, sortDirection) => {
                                handleSort('ROLE', sortDirection);
                            }}
                        >
                            <Typography variant="label" numberOfLines={1}>
                                Role Review
                            </Typography>
                        </TableHeader>
                    )}
                    <TableHeader
                        sortable
                        sortDirection={sortOrder === 'UPDATED_ACCOUNT' ? sortDirection : undefined}
                        onSort={(_event, sortDirection) => {
                            handleSort('UPDATED_ACCOUNT', sortDirection);
                        }}
                        colspan={1}
                    >
                        <Typography variant="label" numberOfLines={1}>
                            Last Updated by
                        </Typography>
                    </TableHeader>
                    <TableHeader
                        sortable
                        sortDirection={sortOrder === 'YEAR' ? sortDirection : undefined}
                        onSort={(_event, sortDirection) => {
                            handleSort('YEAR', sortDirection);
                        }}
                    >
                        <Typography variant="label" numberOfLines={1}>
                            Year
                        </Typography>
                    </TableHeader>
                    <TableHeader
                        sortable
                        sortDirection={sortOrder === 'CARLINE' ? sortDirection : undefined}
                        onSort={(_event, sortDirection) => {
                            handleSort('CARLINE', sortDirection);
                        }}
                    >
                        <Typography variant="label" numberOfLines={1}>
                            Carline
                        </Typography>
                    </TableHeader>
                    <TableHeader
                        sortable
                        sortDirection={sortOrder === 'FAIL_CODE' ? sortDirection : undefined}
                        onSort={(_event, sortDirection) => {
                            handleSort('FAIL_CODE', sortDirection);
                        }}
                    >
                        <Typography variant="label" numberOfLines={1}>
                            Fail Code
                        </Typography>
                    </TableHeader>
                    <TableHeader
                        sortable
                        sortDirection={sortOrder === 'RETAILER' ? sortDirection : undefined}
                        onSort={(_event, sortDirection) => {
                            handleSort('RETAILER', sortDirection);
                        }}
                        hideBorder
                        colspan={1}
                    >
                        <Typography variant="label" numberOfLines={1}>
                            Retailer
                        </Typography>
                    </TableHeader>
                </>
            )}
        </TableRow>
    );
};
